import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { Observable, forkJoin, firstValueFrom } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ApiConfigService } from '../services/api-config.service';

interface StatusTableItem {
  status: string;
  count: number;
}

interface StatusColor {
  status: string;
  color: string;
}

const STATUS_COLORS: StatusColor[] = [
  // Red group
  { status: 'Not started', color: '#ff0000' },
  { status: 'Categorized', color: '#ff0010' },
  
  // Yellow group
  { status: 'Data parsed', color: '#F9FF00' },
  { status: 'Under work', color: '#F3FF00' },
  { status: 'Standardized - Check with BA', color: '#EEFF00' },
  { status: 'Standardized - To be reviewed', color: '#E9FF00' },
  { status: 'Standardized', color: '#E4FF00' },
  
  // Light Green group
  { status: 'Standardized - Ready for validation', color: '#00FFC6' },
  { status: 'Unclear', color: '#20FFC6' },
  
  // Green group
  { status: 'Standardized - Delivered', color: '#00FF28' },
  { status: 'Unclear - Delivered', color: '#00FF48' },
  
  // Grey group
  { status: 'Material Deleted', color: '#A6A6A6' },
  { status: 'Not in scope', color: '#8E8E8E' }
];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: true,
  imports: [BaseChartDirective, CommonModule, FormsModule],
})
export class DashboardComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  // Loading state
  public isLoading = true;

  // Batches for filtering
  public batches: string[] = [];
  public selectedBatch: string = '';

  public tableData: StatusTableItem[] = [];

  // Bar Chart
  public barChartOptions: ChartConfiguration['options'] = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true
      }
    },
    scales: {
      y: {
        ticks: {
          autoSkip: false,
          font: {
            size: 14 // Adjust font size as needed
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 14 // Adjust font size as needed
          }
        }
      }
    }
  };  
  public barChartData: ChartData<'bar', number[], string | string[]> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Statuses'
      },
    ],
  };
  public barChartType: ChartType = 'bar';

  public totalItems: number = 0;

  // Doughnut Chart
  public doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        align: 'center',
        labels: {
          boxWidth: 15,
          padding: 20,
          font: {
            size: 15  // Smaller font size for better fit
          },
          // Add text wrapping for long labels
          textAlign: 'left',
          color: '#333'
        },
        maxWidth: 290,  // Limit legend width
        
      }
    }
  };
  public doughnutChartData: ChartData<'doughnut', number[], string | string[]> = {
    labels: [],
    datasets: [
      {
        data: [],
      },
    ],
  };
  public doughnutChartType: ChartType = 'doughnut';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private apiConfig: ApiConfigService
  ) {}

  ngOnInit() {
    this.fetchBatches().then(
      (batches) => {
        console.log('Batches received in Dashboard component:', batches);
        console.log('Batches array property:', this.batches);
        this.fetchData(this.selectedBatch);
      }
    );
  }

  private async fetchBatches(): Promise<string[]> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.authService.getToken()}`);
    console.log('Fetching batches from:', this.apiConfig.harmonizationBatchesUrl);
    try {
      const batchObjects = await firstValueFrom(this.http.get<any[]>(this.apiConfig.harmonizationBatchesUrl, { headers }));
      console.log('Batches received from API:', batchObjects);
      
      // Extract batch names from objects
      if (Array.isArray(batchObjects) && batchObjects.length > 0) {
        // Check if we're dealing with JSON objects that contain a Batch property
        if (typeof batchObjects[0] === 'object' && batchObjects[0] !== null) {
          // Extract only the Batch property from each object
          this.batches = batchObjects.map(batch => batch.Batch || '');
          console.log('Extracted batch names:', this.batches);
        } else if (typeof batchObjects[0] === 'string') {
          // API returned array of strings
          this.batches = batchObjects;
        }
      } else {
        // Fallback to static batches if API returns empty array
        const { batches: staticBatches } = await import('../../assets/batches');
        this.batches = staticBatches;
        return staticBatches;
      }
      
      return this.batches;
    } catch (error) {
      console.error('Error fetching batches from API:', error);
      console.log('Using static batches data due to API error');
      // Import the static batches data as fallback
      const { batches: staticBatches } = await import('../../assets/batches');
      this.batches = staticBatches;
      return staticBatches;
    }
  }

  onBatchChange() {
    this.fetchData(this.selectedBatch);
  }

  fetchData(selectedBatch: string) {
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const params = selectedBatch ? new HttpParams().set('batch', selectedBatch) : new HttpParams();
  
    this.http.get<any>(this.apiConfig.harmonizationStatusSummaryUrl, { headers, params })
      .subscribe(
        (data) => {
          // Create ordered data based on STATUS_COLORS
          const orderedData: { [key: string]: number } = {};
          const colors: string[] = [];
          
          STATUS_COLORS.forEach(statusColor => {
            if (data.hasOwnProperty(statusColor.status)) {
              orderedData[statusColor.status] = data[statusColor.status];
              colors.push(statusColor.color);
            }
          });
  
          const labels = Object.keys(orderedData);
          const values = Object.values(orderedData);
  
          // Calculate total items
          this.totalItems = values.reduce((sum: number, current: any) => {
            return sum + (typeof current === 'number' ? current : 0);
          }, 0);
  
          // Update bar chart data
          this.barChartData = {
            labels: labels,
            datasets: [{
              data: values as number[],
              label: 'Statuses',
              backgroundColor: colors
            }]
          };
  
          // Update doughnut chart data
          this.doughnutChartData = {
            labels: labels,
            datasets: [{
              data: values as number[],
              backgroundColor: colors
            }]
          };
  
          // Update table data
          this.tableData = labels.map((label, index) => ({
            status: label,
            count: values[index]
          })).filter(item => item.count > 0);
  
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Error fetching data', error);
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
  }
}